/**
 * SELVANE Cart Edit Size v4.0
 * ============================================================
 * Allows customers to change size directly from cart drawer or
 * cart page without navigating back to PDP.
 *
 * v4.0 CHANGES:
 * - Replaced all ILM Size Chart references with SELVANE Size Guide Drawer
 * - triggerILM() → triggerSizeGuide() — opens our Drawer at Tab 2 (Measurements)
 * - openILMDirect() → openSizeGuideDirect() — opens Drawer directly
 * - checkAndOpenILMOnPDP() → checkAndOpenSizeGuideOnPDP() — URL param handler
 * - Removed all ILM modal/button fallback logic
 * ============================================================
 */

window.SelvaneCartEditSizeCooldown = window.SelvaneCartEditSizeCooldown || {
  lastUpdateTime: 0,
  COOLDOWN_MS: 8000
};

class SelvaneCartEditSize {
  constructor() {
    this.modal = document.querySelector('#selvane-change-size-modal');
    if (!this.modal) return;

    this.scrollPosition = 0;
    this.environment = 'unknown';
    this.isUpdating = false;
    this.init();
  }

  init() {
    /* Open modal trigger */
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-selvane-change-size-open]');
      if (trigger) {
        e.preventDefault();
        this.openModal(trigger);
      }
    });

    /* Size Guide trigger inside modal — now opens our Drawer */
    const sizeGuideTrigger = this.modal.querySelector('#selvane-ilm-trigger');
    if (sizeGuideTrigger) {
      sizeGuideTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.triggerSizeGuide();
      });
    }

    /* Close modal */
    this.modal.querySelectorAll('[data-selvane-modal-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeModal());
    });
    this.modal.querySelector('[data-selvane-modal-overlay]')?.addEventListener('click', () => this.closeModal());

    /* Update size button */
    const updateBtn = this.modal.querySelector('[data-selvane-change-size-update]');
    if (updateBtn) {
      updateBtn.addEventListener('click', () => this.updateSize());
    }

    /* Check URL param for opening size guide on PDP load */
    this.checkAndOpenSizeGuideOnPDP();
  }

  /* ================================================================
     COOLDOWN MANAGEMENT
     ================================================================ */

  isInCooldown() {
    const cooldown = window.SelvaneCartEditSizeCooldown;
    return Date.now() - cooldown.lastUpdateTime < cooldown.COOLDOWN_MS;
  }

  getRemainingCooldown() {
    const cooldown = window.SelvaneCartEditSizeCooldown;
    const elapsed = Date.now() - cooldown.lastUpdateTime;
    const remaining = Math.max(0, cooldown.COOLDOWN_MS - elapsed);
    return Math.ceil(remaining / 1000);
  }

  startCooldown() {
    window.SelvaneCartEditSizeCooldown.lastUpdateTime = Date.now();
  }

  /* ================================================================
     MODAL OPEN / CLOSE
     ================================================================ */

  async openModal(trigger) {
    const handle = trigger.getAttribute('data-product-handle');
    const currentSize = trigger.getAttribute('data-current-size');
    const currentColor = trigger.getAttribute('data-current-color');
    const variantId = trigger.getAttribute('data-variant-id');
    const lineItemKey = trigger.getAttribute('data-line-item-key');
    const qty = trigger.getAttribute('data-quantity');

    this.environment = trigger.getAttribute('data-environment') || 'unknown';

    this.modal.querySelector('[data-selvane-change-size-title]').textContent =
      trigger.getAttribute('data-product-title') || '';
    this.modal.querySelector('[data-selvane-change-size-image]').src =
      trigger.getAttribute('data-product-image') || '';
    this.modal.querySelector('[data-selvane-change-size-current]').textContent =
      currentSize || '';

    const colorDisplay = this.modal.querySelector('[data-selvane-change-size-color]');
    if (colorDisplay) {
      colorDisplay.textContent = currentColor || 'Default';
    }

    this.modal.setAttribute('data-current-variant-id', variantId || '');
    this.modal.setAttribute('data-line-item-key', lineItemKey || '');
    this.modal.setAttribute('data-line-item-qty', qty || '');
    this.modal.setAttribute('data-current-size', currentSize || '');
    this.modal.setAttribute('data-current-color', currentColor || '');
    this.modal.setAttribute('data-environment', this.environment);
    this.modal.setAttribute('data-product-handle', handle || '');

    /* Show the Size Guide link */
    const sizeGuideTrigger = this.modal.querySelector('#selvane-ilm-trigger');
    if (sizeGuideTrigger) {
      sizeGuideTrigger.style.display = 'inline';
    }

    this.scrollPosition = window.scrollY || window.pageYOffset;
    this.modal.setAttribute('aria-hidden', 'false');
    this.resetUpdateButton();
    await this.loadProductData(handle, currentSize, currentColor);
  }

  closeModal() {
    this.modal.setAttribute('aria-hidden', 'true');
    window.scrollTo(0, this.scrollPosition);
  }

  resetUpdateButton() {
    const updateBtn = this.modal.querySelector('[data-selvane-change-size-update]');
    if (updateBtn) {
      if (this.isInCooldown()) {
        const remaining = this.getRemainingCooldown();
        updateBtn.textContent = `Wait ${remaining}s...`;
        updateBtn.disabled = true;
        updateBtn.classList.add('is-cooldown');
        this.startCooldownCountdown(updateBtn);
      } else {
        updateBtn.textContent = 'Update size';
        updateBtn.disabled = true;
        updateBtn.classList.remove('is-success', 'is-cooldown');
      }
    }
    this.isUpdating = false;
  }

  startCooldownCountdown(updateBtn) {
    const interval = setInterval(() => {
      if (!this.isInCooldown()) {
        clearInterval(interval);
        updateBtn.textContent = 'Update size';
        updateBtn.classList.remove('is-cooldown');
        if (this.modal.querySelector('.selvane-change-size__option.is-selected') && !this.isUpdating) {
          updateBtn.disabled = false;
        }
      } else {
        const remaining = this.getRemainingCooldown();
        updateBtn.textContent = `Wait ${remaining}s...`;
      }
    }, 500);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'aria-hidden' &&
            this.modal.getAttribute('aria-hidden') === 'true') {
          clearInterval(interval);
          observer.disconnect();
        }
      });
    });
    observer.observe(this.modal, { attributes: true });
  }

  /* ================================================================
     PRODUCT DATA LOADING
     ================================================================ */

  async loadProductData(handle, currentSize, currentColor) {
    const optionsContainer = this.modal.querySelector('[data-selvane-change-size-options]');
    optionsContainer.innerHTML = '<div class="selvane-loading-dots">...</div>';

    try {
      const data = await (await fetch(`/products/${handle}.js`)).json();
      this.renderOptions(data, currentSize, currentColor);
    } catch (error) {
      console.error('Error loading product:', error);
      optionsContainer.innerHTML = '<p>Error loading sizes.</p>';
    }
  }

  renderOptions(product, currentSize, currentColor) {
    const optionsContainer = this.modal.querySelector('[data-selvane-change-size-options]');
    optionsContainer.innerHTML = '';

    const sizeIndex = product.options.findIndex(
      (o) => o.name.toLowerCase().includes('size') || o.name.toLowerCase() === '\u5C3A\u7801'
    );
    const colorIndex = product.options.findIndex(
      (o) => o.name.toLowerCase().includes('color') || o.name.toLowerCase() === '\u989C\u8272'
    );

    if (sizeIndex === -1) {
      optionsContainer.innerHTML = '<p>No size options available.</p>';
      return;
    }

    let filteredVariants = product.variants;
    if (currentColor && colorIndex !== -1) {
      filteredVariants = product.variants.filter((v) => v.options[colorIndex] === currentColor);
      if (filteredVariants.length === 0) {
        filteredVariants = product.variants;
      }
    }

    const seenSizes = new Set();
    filteredVariants.forEach((variant) => {
      const sizeValue = variant.options[sizeIndex];
      if (seenSizes.has(sizeValue)) return;
      seenSizes.add(sizeValue);

      const btn = document.createElement('button');
      btn.className = 'selvane-change-size__option';
      if (sizeValue === currentSize) btn.classList.add('is-selected');
      if (variant.available) {
        btn.onclick = () => this.selectSize(btn, variant.id);
      } else {
        btn.classList.add('is-disabled');
      }
      btn.textContent = sizeValue;
      optionsContainer.appendChild(btn);
    });
  }

  selectSize(btn, variantId) {
    this.modal.querySelectorAll('.selvane-change-size__option').forEach((b) => b.classList.remove('is-selected'));
    btn.classList.add('is-selected');

    const updateBtn = this.modal.querySelector('[data-selvane-change-size-update]');
    if (!this.isUpdating && !this.isInCooldown()) {
      updateBtn.disabled = false;
    }
    updateBtn.setAttribute('data-new-variant-id', String(variantId));
  }

  /* ================================================================
     SIZE UPDATE (Cart API)
     ================================================================ */

  async updateSize() {
    const updateBtn = this.modal.querySelector('[data-selvane-change-size-update]');

    if (this.isInCooldown()) {
      const remaining = this.getRemainingCooldown();
      console.log(`SELVANE: In cooldown, ${remaining}s remaining`);
      updateBtn.textContent = `Wait ${remaining}s...`;
      return;
    }

    if (this.isUpdating) {
      console.log('SELVANE: Update in progress, ignoring click');
      return;
    }

    const newVariantId = updateBtn.getAttribute('data-new-variant-id');
    const currentVariantId = this.modal.getAttribute('data-current-variant-id');
    const lineItemKey = this.modal.getAttribute('data-line-item-key');
    const env = this.modal.getAttribute('data-environment');

    this.isUpdating = true;
    updateBtn.disabled = true;
    updateBtn.textContent = 'Updating...';

    try {
      await this.replaceVariantKeepQty({
        currentVariantId,
        newVariantId,
        lineItemKey,
        fallbackQty: this.modal.getAttribute('data-line-item-qty')
      });

      this.startCooldown();
      updateBtn.textContent = 'Updated \u2713';
      updateBtn.classList.add('is-success');
      this.closeModal();

      if (env === 'drawer') {
        await this.refreshDrawer();
      } else {
        await this.refreshCartPage();
      }
    } catch (error) {
      console.error('SELVANE: Update failed:', error);

      if (error.message && (error.message.includes('429') || error.message.includes('rate'))) {
        window.SelvaneCartEditSizeCooldown.lastUpdateTime = Date.now();
        window.SelvaneCartEditSizeCooldown.COOLDOWN_MS = 10000;
        const remaining = this.getRemainingCooldown();
        updateBtn.textContent = `Rate limited. Wait ${remaining}s`;
        updateBtn.classList.add('is-cooldown');
        this.startCooldownCountdown(updateBtn);
      } else {
        alert('Failed to update size. Please try again.');
        updateBtn.disabled = false;
        updateBtn.textContent = 'Update size';
      }
      this.isUpdating = false;
    }
  }

  /* ================================================================
     FETCH WITH RETRY (Rate limit handling)
     ================================================================ */

  async fetchWithRetry(url, options, maxRetries = 3) {
    let lastError;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);

        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          let waitTime;
          if (retryAfter) {
            waitTime = isNaN(retryAfter)
              ? new Date(retryAfter).getTime() - Date.now()
              : parseInt(retryAfter, 10) * 1000;
          } else {
            waitTime = 1000 * Math.pow(2, attempt);
          }
          console.warn(`SELVANE: Rate limited (429). Waiting ${waitTime}ms before retry ${attempt + 1}/${maxRetries}`);
          if (attempt < maxRetries) {
            await this.delay(waitTime);
            continue;
          }
          throw new Error('Rate limit exceeded (429). Please wait and try again.');
        }

        if (!response.ok) {
          const text = await response.text();
          if (text.includes('Verifying your connection') || text.includes('cf_chl')) {
            const waitTime = 1000 * Math.pow(2, attempt);
            console.warn(`SELVANE: Cloudflare challenge. Waiting ${waitTime}ms before retry`);
            if (attempt < maxRetries) {
              await this.delay(waitTime);
              lastError = new Error('Cloudflare challenge detected');
              continue;
            }
            throw new Error('Cloudflare challenge: Please refresh the page and try again');
          }
          throw new Error(`Request failed: ${text.substring(0, 200)}`);
        }

        return response;
      } catch (error) {
        if (error.name === 'TypeError' && attempt < maxRetries) {
          const waitTime = 1000 * Math.pow(2, attempt);
          console.warn(`SELVANE: Network error. Waiting ${waitTime}ms before retry`);
          await this.delay(waitTime);
          continue;
        }
      }
    }
    throw lastError || new Error('Max retries exceeded');
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /* ================================================================
     CART VARIANT REPLACEMENT
     ================================================================ */

  async replaceVariantKeepQty({ currentVariantId, newVariantId, lineItemKey, fallbackQty }) {
    const cart = await (await fetch('/cart.js')).json();
    let item = null;

    if (lineItemKey) {
      item = (cart.items || []).find((it) => it.key === lineItemKey);
    }
    if (!item) {
      const idNum = Number(currentVariantId);
      item = (cart.items || []).find((it) => Number(it.variant_id) === idNum);
    }
    if (!item) {
      throw new Error(`Current item not found in cart.js (variant_id=${currentVariantId}, key=${lineItemKey})`);
    }

    const key = item.key;
    const qty = Number(item.quantity || fallbackQty || 1);

    console.log('SELVANE: Removing old variant...');
    const removeResp = await this.fetchWithRetry('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    }, 3);
    if (!removeResp.ok) {
      const t = await removeResp.text();
      throw new Error('Remove failed: ' + t);
    }

    console.log('SELVANE: Adding new variant...');
    const addResp = await this.fetchWithRetry('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(newVariantId), quantity: qty }] })
    }, 3);
    if (!addResp.ok) {
      const t = await addResp.text();
      throw new Error('Add failed: ' + t);
    }

    console.log('SELVANE: Size update completed successfully');
  }

  /* ================================================================
     CART REFRESH
     ================================================================ */

  async refreshDrawer() {
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        await cartDrawer.refresh();
        this.reinjectButtons();
        return;
      }
      const response = await fetch('/?sections=cart-drawer');
      if (response.ok) {
        const data = await response.json();
        if (data['cart-drawer']) {
          const newContent = new DOMParser()
            .parseFromString(data['cart-drawer'], 'text/html')
            .querySelector('cart-drawer');
          if (newContent && cartDrawer) {
            cartDrawer.innerHTML = newContent.innerHTML;
            this.reinjectButtons();
            return;
          }
        }
      }
      window.location.reload();
    } catch (e) {
      console.error('SELVANE: Drawer refresh failed:', e);
      window.location.reload();
    }
  }

  async refreshCartPage() {
    try {
      const text = await (await fetch(window.location.pathname)).text();
      const newCartItems = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('.cart-items');
      const oldCartItems = document.querySelector('.cart-items');
      if (newCartItems && oldCartItems) {
        oldCartItems.innerHTML = newCartItems.innerHTML;
        this.reinjectButtons();
        return;
      }
      window.location.reload();
    } catch (e) {
      console.error('SELVANE: Cart page refresh failed:', e);
      window.location.reload();
    }
  }

  reinjectButtons() {
    document.querySelectorAll('.selvane-injected').forEach((el) => el.classList.remove('selvane-injected'));
    document.querySelectorAll('.selvane-adjust-size-trigger-v3').forEach((btn) => btn.remove());
  }

  /* ================================================================
     SIZE GUIDE INTEGRATION (replaces ILM)
     ================================================================
     v4.0: Opens our SELVANE Size Guide Drawer instead of ILM.
     - From cart drawer: opens Drawer directly at Tab 2 (Measurements)
     - From cart page: navigates to PDP with ?open_sizeguide=true
     ================================================================ */

  triggerSizeGuide() {
    if (this.environment === 'drawer') {
      /* In cart drawer context: close Adjust Size modal, then open our Drawer */
      this.closeModal();
      this.openSizeGuideDirect();
      return;
    }

    if (this.environment === 'cart') {
      /* On cart page: navigate to PDP with param to auto-open Size Guide */
      const handle = this.modal.getAttribute('data-product-handle');
      if (handle) {
        window.location.href = `/products/${handle}?open_sizeguide=true`;
        return;
      }
    }

    /* Fallback: try to open directly */
    this.closeModal();
    this.openSizeGuideDirect();
  }

  openSizeGuideDirect() {
    /* Find our Size Guide Drawer and open it at Tab 2 (Measurements) */
    const drawer = document.querySelector('[data-size-guide-drawer]');
    if (drawer) {
      /* Use a small delay to let the Adjust Size modal close first */
      setTimeout(() => {
        /* Dispatch a custom event that our Drawer JS listens for */
        document.dispatchEvent(new CustomEvent('selvane:open-size-guide', {
          detail: { tab: 'measurements' }
        }));

        /* Fallback: directly manipulate if custom event doesn't work */
        if (drawer.hidden) {
          const trigger = document.querySelector('[data-size-guide-trigger]');
          if (trigger) {
            trigger.click();
            /* After opening, switch to measurements tab */
            setTimeout(() => {
              const measTab = drawer.querySelector('[data-tab="measurements"]');
              if (measTab) measTab.click();
            }, 100);
          }
        }
      }, 200);
      return;
    }

    /* If Drawer not found on this page (e.g., cart page without PDP),
       navigate to PDP */
    const handle = this.modal.getAttribute('data-product-handle');
    if (handle) {
      window.location.href = `/products/${handle}?open_sizeguide=true`;
    }
  }

  checkAndOpenSizeGuideOnPDP() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('open_sizeguide') !== 'true') return;

    /* Wait for our Size Guide Drawer to be initialized */
    const checkDrawer = setInterval(() => {
      const drawer = document.querySelector('[data-size-guide-drawer]');
      if (drawer) {
        clearInterval(checkDrawer);
        setTimeout(() => {
          /* Dispatch custom event to open at Measurements tab */
          document.dispatchEvent(new CustomEvent('selvane:open-size-guide', {
            detail: { tab: 'measurements' }
          }));

          /* Fallback: click trigger */
          if (drawer.hidden) {
            const trigger = document.querySelector('[data-size-guide-trigger]');
            if (trigger) {
              trigger.click();
              setTimeout(() => {
                const measTab = drawer.querySelector('[data-tab="measurements"]');
                if (measTab) measTab.click();
              }, 100);
            }
          }

          /* Clean URL param */
          if (window.history && window.history.replaceState) {
            const cleanUrl = window.location.pathname + window.location.hash;
            window.history.replaceState({}, '', cleanUrl);
          }
        }, 500);
      }
    }, 100);

    /* Safety timeout */
    setTimeout(() => clearInterval(checkDrawer), 5000);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new SelvaneCartEditSize();
});
